import { Fragment, useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useGoals } from '../contexts/GoalsContext';
import CircularProgress from './CircularProgress';

export default function GoalSheet({ isOpen, onClose, goal = null }) {
  const categories = useMemo(() => [
    { name: 'Wellness', icon: '🌱' },
    { name: 'Learning', icon: '📚' },
    { name: 'Career', icon: '💼' },
    { name: 'Creativity', icon: '🎨' },
    { name: 'Fitness', icon: '💪' },
    { name: 'Mindfulness', icon: '🧘' },
  ], []);

  const { addGoal, updateGoal, deleteGoal, updateGoalProgress } = useGoals();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    targetDate: '',
    description: '',
    subGoals: []
  });
  const [newSubGoal, setNewSubGoal] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [localProgress, setLocalProgress] = useState(goal?.progress || 0);

  // Initialize form data when editing existing goal
  useEffect(() => {
    if (goal) {
      setFormData({
        title: goal.title,
        category: goal.category,
        targetDate: goal.targetDate,
        description: goal.description || '',
        subGoals: goal.subGoals || []
      });
      setLocalProgress(goal.progress);
      setSelectedCategory(categories.find(c => c.name === goal.category));
    } else {
      // Reset form for new goal
      setFormData({
        title: '',
        category: '',
        targetDate: '',
        description: '',
        subGoals: []
      });
      setLocalProgress(0);
      setSelectedCategory(null);
    }
  }, [goal, categories]);

  const handleInputChange = async (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    // Auto-save if editing existing goal
    if (goal) {
      try {
        await updateGoal(goal.id, {
          ...formData,
          [id]: value
        });
      } catch (error) {
        console.error('Error auto-saving goal:', error);
      }
    }
  };

  const handleAddSubGoal = async () => {
    if (!newSubGoal.trim() || !goal) return;
    
    const updatedSubGoals = [...formData.subGoals, { text: newSubGoal.trim(), completed: false }];
    setFormData(prev => ({
      ...prev,
      subGoals: updatedSubGoals
    }));
    setNewSubGoal('');

    try {
      await updateGoal(goal.id, {
        ...formData,
        subGoals: updatedSubGoals
      });
    } catch (error) {
      console.error('Error auto-saving sub-goal:', error);
    }
  };

  const handleToggleSubGoal = async (index) => {
    const updatedSubGoals = formData.subGoals.map((sg, i) => 
      i === index ? { ...sg, completed: !sg.completed } : sg
    );
    
    setFormData(prev => ({
      ...prev,
      subGoals: updatedSubGoals
    }));

    if (goal) {
      try {
        await updateGoal(goal.id, {
          ...formData,
          subGoals: updatedSubGoals
        });
      } catch (error) {
        console.error('Error auto-saving sub-goal toggle:', error);
      }
    }
  };

  const handleRemoveSubGoal = async (index) => {
    const updatedSubGoals = formData.subGoals.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      subGoals: updatedSubGoals
    }));

    if (goal) {
      try {
        await updateGoal(goal.id, {
          ...formData,
          subGoals: updatedSubGoals
        });
      } catch (error) {
        console.error('Error auto-saving sub-goal removal:', error);
      }
    }
  };

  const handleCategorySelect = async (category) => {
    setSelectedCategory(category);
    setFormData(prev => ({
      ...prev,
      category: category.name
    }));

    // Auto-save if editing existing goal
    if (goal) {
      try {
        await updateGoal(goal.id, {
          ...formData,
          category: category.name
        });
      } catch (error) {
        console.error('Error auto-saving category:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.category || !formData.targetDate) return;

    setIsSubmitting(true);
    try {
      const goalData = {
        ...formData,
        progress: 0,
        status: 'active',
        isPrivate: isPrivate,
        lastModified: new Date(),
      };

      if (goal) {
        // Update existing goal
        await updateGoal(goal.id, goalData);
      } else {
        // Create new goal
        await addGoal(goalData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving goal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!goal || !window.confirm('Are you sure you want to delete this goal?')) return;
    
    try {
      await deleteGoal(goal.id);
      onClose();
    } catch (error) {
      console.error('Error deleting goal:', error);
    }
  };

  const handlePrivacyToggle = async () => {
    const newPrivateState = !isPrivate;
    setIsPrivate(newPrivateState);
    
    if (goal) {
      try {
        await updateGoal(goal.id, {
          ...formData,
          isPrivate: newPrivateState
        });
      } catch (error) {
        console.error('Error auto-saving privacy setting:', error);
      }
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* ... Dialog overlay ... */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="flex items-end min-h-full">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="w-full h-[85vh] transform overflow-hidden bg-white rounded-t-2xl shadow-xl transition-all">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                  <div className="flex justify-center py-2">
                    <div className="w-12 h-1.5 rounded-full bg-gray-300" />
                  </div>
                  <div className="flex items-center justify-between px-4 pb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      {goal ? 'Edit Goal' : 'New Goal'}
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                    >
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="px-4 py-6 space-y-6 overflow-y-auto" style={{ height: 'calc(85vh - 76px)' }}>
                  {/* Goal Title */}
                  <div className="space-y-2">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      What's your goal?
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent select-text"
                      placeholder="Enter goal title"
                    />
                  </div>

                  {/* Category Selection */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {categories.map((category) => (
                        <button
                          key={category.name}
                          onClick={() => handleCategorySelect(category)}
                          className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors
                            ${selectedCategory?.name === category.name 
                              ? 'border-primary-500 bg-primary-50' 
                              : 'border-transparent hover:border-primary-500 hover:bg-primary-50'
                            }`}
                        >
                          <span className="text-2xl">{category.icon}</span>
                          <span className="text-sm font-medium text-gray-600">{category.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Target Date */}
                  <div className="space-y-2">
                    <label htmlFor="targetDate" className="block text-sm font-medium text-gray-700">
                      Target Date
                    </label>
                    <input
                      type="date"
                      id="targetDate"
                      value={formData.targetDate}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description (Optional)
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Add any notes or details about your goal..."
                    />
                  </div>

                  {/* Sub-goals */}
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Sub-goals
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newSubGoal}
                        onChange={(e) => setNewSubGoal(e.target.value)}
                        placeholder="Add a sub-goal"
                        className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        onKeyPress={(e) => e.key === 'Enter' && handleAddSubGoal()}
                      />
                      <button
                        onClick={handleAddSubGoal}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.subGoals.map((subGoal, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <button
                            onClick={() => handleToggleSubGoal(index)}
                            className={`flex-shrink-0 w-5 h-5 rounded border ${
                              subGoal.completed 
                                ? 'bg-primary-500 border-primary-500' 
                                : 'border-gray-300'
                            }`}
                          >
                            {subGoal.completed && (
                              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                          <span className={`flex-1 ${subGoal.completed ? 'line-through text-gray-500' : ''}`}>
                            {subGoal.text}
                          </span>
                          <button
                            onClick={() => handleRemoveSubGoal(index)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Progress Control (only for existing goals) */}
                  {goal && (
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Progress
                      </label>
                      <div className="flex items-center justify-between bg-gray-50 p-4 rounded-xl">
                        <button
                          onClick={async () => {
                            const newProgress = Math.max(0, localProgress - 5);
                            setLocalProgress(newProgress);
                            await updateGoalProgress(goal.id, newProgress);
                          }}
                          className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                        >
                          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                        </button>

                        <CircularProgress progress={localProgress} size={60} strokeWidth={6} />

                        <button
                          onClick={async () => {
                            const newProgress = Math.min(100, localProgress + 5);
                            setLocalProgress(newProgress);
                            await updateGoalProgress(goal.id, newProgress);
                          }}
                          className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                        >
                          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4">
                    {goal ? (
                      <button
                        onClick={handleDelete}
                        className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                      >
                        Delete Goal
                      </button>
                    ) : (
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Creating...' : 'Create Goal'}
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between py-4 border-t border-gray-100">
                    <div className="flex items-center gap-3">
                      <svg 
                        className={`w-5 h-5 ${isPrivate ? 'text-gray-400' : 'text-primary-500'}`} 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d={isPrivate 
                            ? "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            : "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          } 
                        />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {isPrivate ? 'Private Goal' : 'Visible to Friends'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {isPrivate 
                            ? 'Only you can see this goal' 
                            : 'Friends can see and support your progress'
                          }
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handlePrivacyToggle}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        isPrivate ? 'bg-gray-200' : 'bg-primary-500'
                      }`}
                    >
                      <span className="sr-only">Toggle goal privacy</span>
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          isPrivate ? 'translate-x-0' : 'translate-x-5'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
} 